import React, { useState } from "react"
import { HiChevronDown, HiPause, HiPlay } from "react-icons/hi2"
import { IconChevronDown } from "@tabler/icons-react"
import Title from "@love-irish/core-title"
import Tooltip from "design_system/core/components/tooltip"
import { ResourceActions } from "@love-irish/resource-list-item"
import { IconOrEmoji } from "@love-irish/resource-server-icon"
import { PronunciationLabel } from "@love-irish/resource-pronunciation-label"
import { twMerge } from "tailwind-merge"
import type { Collection, CollectionSection } from "@love-irish/types"

import { themeForSection, type SceneTheme } from "./themes"
import { SceneBackground, type CharacterKey } from "./scene"
import { VocabCarousel } from "./vocab_carousel"
import { useCollectionSectionToggle } from "./use_collection_section_toggle"

export {
  TIME_THEMES,
  EVENT_THEMES,
  resolveTheme,
  themeForSection,
  currentTimeKey,
  currentEvent,
} from "./themes"
export type { SceneTheme, TimeKey, SceneImage } from "./themes"
export type { CharacterKey } from "./scene"
export { VocabCarousel } from "./vocab_carousel"
export { SceneBackground, DefaultScene, Celestial, Starfield } from "./scene"
export { useCollectionSectionToggle } from "./use_collection_section_toggle"
export { ConnectedDailyScene } from "./connected"

// A section's key is its display label with the first letter cased ("morning" -> "Morning").
const humanize = (key = "") =>
  key ? key.charAt(0).toUpperCase() + key.slice(1).replace(/[-_]/g, " ") : key

export interface DailySceneProps {
  collection?: Collection
  /** The collection's sections (each a time of day, event, etc.). */
  sections?: CollectionSection[]
  /** Deterministic starting section (stories/tests); otherwise the live clock. */
  initialSectionKey?: string
  /** Override the section's greeting text. */
  greeting?: { irish?: string; english?: string; emoji?: string }
  subtitle?: string
  /** Which pixel character to show if the pixel scene slot is used. */
  character?: CharacterKey
  /** Swap the illustration entirely (photo / pixel / Rive). */
  renderScene?: (theme: SceneTheme) => React.ReactNode
  /** Ambient "TV" mode: forces the vocab carousel to autoplay regardless of per-section settings. */
  tv?: boolean
  className?: string
}

/** Caret dropdown that switches the active section. Selecting one pauses the live clock. */
const SectionMenu: React.FC<{
  sections: readonly CollectionSection[]
  activeKey: string
  dark: boolean
  onSelect: (key: string) => void
}> = ({ sections, activeKey, dark, onSelect }) => {
  const [open, setOpen] = useState(false)

  const activeSection = sections.find((s) => s.sectionKey === activeKey)

  const trigger = dark
    ? "bg-white/10 text-white hover:bg-white/25 border-white/20"
    : "bg-black/5 text-primary-800 hover:bg-black/10 border-black/10"

  return (
    <div className="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change time of day"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black uppercase tracking-wide border ${trigger}`}
      >
        {humanize(activeKey)}
        <IconChevronDown className="h-3.5 w-3.5" stroke={3} />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <ul
            role="listbox"
            className="absolute left-0 z-20 mt-1 w-44 overflow-hidden rounded-lg border border-black/10 bg-white py-1 shadow-lg"
          >
            {sections.map((section) => (
              <li
                key={section.sectionKey}
                role="option"
                aria-selected={section.sectionKey === activeKey}
              >
                <button
                  type="button"
                  onClick={() => {
                    onSelect(section.sectionKey)
                    setOpen(false)
                  }}
                  className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-sm hover:bg-primary-50 ${
                    section.sectionKey === activeKey
                      ? "font-semibold text-primary-900"
                      : "text-primary-700"
                  }`}
                >
                  <IconOrEmoji
                    icon={section.settings?.icon as string | undefined}
                    emoji={section.settings?.emoji as string | undefined}
                    size={15}
                  />
                  {humanize(section.sectionKey)}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * DailyScene
 *
 * A full-width, section-driven hero. The active section (chosen by the live time
 * of day, or by the caret dropdown) supplies the sky, the header text/translation
 * (name / name_en), and a carousel of its codex records. Picking a section from the
 * dropdown pauses the live clock; the play button resumes it. Sections are the
 * server CollectionSection type — no remapping. The illustration is a swappable
 * slot via `renderScene`.
 */
export const DailyScene: React.FC<DailySceneProps> = ({
  sections = [],
  initialSectionKey,
  collection,
  greeting,
  subtitle,
  character = "grug",
  renderScene,
  tv = false,
  className = "",
}) => {
  const { active, activeKey, paused, select, resume } =
    useCollectionSectionToggle(sections, { initialKey: initialSectionKey })

  const theme = themeForSection(activeKey)
  const dark = !!theme.dark
  const muted = dark ? "text-white/80" : "text-primary-700"
  const controlBtn = dark
    ? "bg-white/15 text-white hover:bg-white/25"
    : "bg-black/5 text-primary-800 hover:bg-black/10"

  const emoji = greeting?.emoji ?? active?.settings?.emoji
  const irish = greeting?.irish ?? active?.name
  const english = greeting?.english ?? active?.nameEn
  const pronunciation = active?.pronunciation
  const sub = subtitle ?? active?.description
  const collectionItems = active?.collectionItems ?? []
  // /tv forces autoplay; otherwise a section opts in via its `autoplay` carousel setting.
  const autoplay = tv || active?.settings?.autoplay === true

  return (
    <div
      className={`${
        tv ? "min-h-screen" : "min-h-[1000px]"
      } bg-center flex w-full justify-content-around ${
        theme.gradient
      } ${className}`}
    >
      <section
        className={`relative w-full ${tv ? "" : "max-w-[2000px] mx-auto"}`}
        aria-label={`Daily scene: ${theme.label}`}
      >
        {renderScene ? (
          renderScene(theme)
        ) : (
          <SceneBackground theme={theme} fullscreen={tv} />
        )}

        {/* Top-right controls: save / add-to-collection (the scene surfaces a savable collection) + pause/play */}
        <div className="absolute right-4 top-4 z-20 flex items-center gap-2">
          {/* Pause / play the live clock */}
          <Tooltip
            title={
              paused
                ? "Paused on this scene. Play to follow the time of day again."
                : "This scene changes through the day. Pause to hold it, or pick a time from the menu."
            }
          >
            <button
              type="button"
              onClick={() => (paused ? resume() : select(activeKey))}
              aria-label={
                paused ? "Resume automatic time" : "Pause automatic time"
              }
              className={`rounded-full p-2 shadow-md ${controlBtn}`}
            >
              {paused ? (
                <HiPlay className="h-4 w-4" />
              ) : (
                <HiPause className="h-4 w-4" />
              )}
            </button>
          </Tooltip>

          <div className={dark ? "dark" : ""}>
            {collection && <ResourceActions item={collection} />}
          </div>
        </div>

        <div className="container relative z-10 py-10 md:pt-44 md:pb-24 flex flex-col justify-content-around h-full">
          {/* Header + text, left aligned — text comes from the active section */}
          <div className={dark ? "dark" : ""}>
            {sections.length > 0 && (
              <SectionMenu
                sections={sections}
                activeKey={activeKey}
                dark={dark}
                onSelect={select}
              />
            )}

            <div className="text-shadow-sm text-shadow-white/15 dark:text-shadow-black/20">
              {(irish || emoji) && (
                <div>
                  <div className="mt-3 flex gap-5 items-center">
                    {emoji && <span className="text-9xl hidden">{emoji}</span>}
                    {irish && (
                      <Title
                        as="h2"
                        size="xl"
                        title={irish}
                        variant="fancy"
                        className="max-md:text-4xl"
                      />
                    )}
                  </div>
                  {(english || pronunciation) && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {english && (
                        <p
                          className={`${muted} italic text-xl bg-background/60 backdrop-blur-sm rounded py-1 px-2 max-w-xl`}
                        >
                          &ldquo;{english}&rdquo;
                        </p>
                      )}
                      <PronunciationLabel
                        pronunciation={pronunciation}
                        className={`${muted} font-mono text-lg backdrop-blur-sm`}
                      />
                    </div>
                  )}
                </div>
              )}

              {sub && (
                <p
                  className={`bg-background/60 backdrop-blur-sm rounded py-1 px-2 inline-block mt-4 ${muted} max-w-lg`}
                >
                  {sub}
                </p>
              )}

              {collection?.path && (
                <div>
                  <p
                    className={`bg-background/60 backdrop-blur-sm rounded py-1 px-2 inline-block mt-4 font-black ${muted}`}
                  >
                    <a href={collection.path}>View more &rarr;</a>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Vocab carousel: its own full-width row inside the container */}
          {collectionItems.length > 0 && (
            <div className=" w-full">
              <VocabCarousel
                collectionItems={collectionItems}
                dark={dark}
                resetKey={activeKey}
                autoplay={autoplay}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default DailyScene
